import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import Loader from '../../components/Loader';
import AuthService from '../../services/AuthService';
import ImageUpload from '../../components/ImageUpload';
import FileDropzone from '../../components/FileDropzone';
import GoogleMapSelector from '../../components/GoogleMapSelector';
import { useNavigate } from 'react-router-dom';

const DOC_FIELDS = [
  { key: 'business_license',       label: 'Business License' },
  { key: 'emirates_id',            label: 'Emirates ID' },
  { key: 'trade_name_certificate', label: 'Trade Name Certificate' },
];

const BANK_FIELDS = [
  { key: 'account_title',  label: 'Account Title' },
  { key: 'account_number', label: 'Account Number' },
  { key: 'iban',           label: 'IBAN' },
  { key: 'bank_name',      label: 'Bank Name' },
  { key: 'city',           label: 'City' },
];

const EMPTY_BANK = { account_title: '', account_number: '', iban: '', bank_name: '', city: '' };

const PartnerProfile = () => {
  const navigate = useNavigate();

  const [tab, setTab] = useState('info');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingDocs, setSavingDocs] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);

  // Bank details
  const [bank, setBank] = useState(EMPTY_BANK);
  const [bankLoaded, setBankLoaded] = useState(false);
  const [savingBank, setSavingBank] = useState(false);

  const [form, setForm] = useState({
    company_name: '', owner_name: '', email: '', phone_code: '', phone: '',
    website: '', business_type_id: '', address: '', about: '', lat: '', lng: '',
    order_accept_type: 'Manual', logo: null,
  });

  // Cover images: kept old URLs + new files; remember the originals to compute removals
  const originalCovers = useRef([]);
  const [oldCoverImages, setOldCoverImages] = useState([]);
  const [coverFiles, setCoverFiles] = useState([]);

  // Location for the map selector
  const [mapLocation, setMapLocation] = useState(null);

  // Documents
  const [documents, setDocuments] = useState(null);
  const [docFiles, setDocFiles] = useState({ business_license: null, emirates_id: null, trade_name_certificate: null });

  useEffect(() => {
    if (!AuthService.getCurrentpartner()) { navigate('/partner/login'); return; }
    getProfile();
    getBusinessTypes();
  }, []);

  const getBusinessTypes = async () => {
    try {
      const res = await ApiService.request({ method: 'GET', url: 'partner/getBusinessTypes' });
      if (res.data.status) setBusinessTypes(res.data.data || []);
    } catch { /* non-blocking */ }
  };

  const getProfile = async () => {
    try {
      setLoading(true);
      const res = await ApiService.request({ method: 'GET', url: 'partner/getProfile' });
      if (res.data.status) {
        const c = res.data.data.partner;
        setProfile(c);
        setForm({
          company_name:     c.company_name || '',
          owner_name:       c.owner_name   || '',
          email:            c.email        || '',
          phone_code:       c.phone_code   || '',
          phone:            c.phone        || '',
          website:          c.website      || '',
          business_type_id: c.business_type_id?._id || c.business_type_id || '',
          address:          c.address      || '',
          about:            c.about        || '',
          lat:              c.lat ?? '',
          lng:              c.lng ?? '',
          order_accept_type: c.order_accept_type || 'Manual',
          logo:             c.logo || null,
        });
        const covers = Array.isArray(c.cover_images) ? c.cover_images : [];
        originalCovers.current = covers;
        setOldCoverImages(covers);
        if (c.address || (c.lat && c.lng)) {
          setMapLocation({ text: c.address || '', lat: c.lat || null, lng: c.lng || null });
        }
        setDocuments(res.data.data.documents || null);
      } else toast.error(res.data.message);
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  };

  const onChange = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  const handleLogoChange = (name, file) => setForm(p => ({ ...p, logo: file }));

  const handleLocationChanged = useCallback((loc) => {
    setMapLocation(loc);
    setForm(prev => ({ ...prev, address: loc.text, lat: loc.lat, lng: loc.lng }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const fd = new FormData();
      fd.append('company_name', form.company_name);
      fd.append('owner_name', form.owner_name);
      fd.append('email', form.email);
      fd.append('phone_code', form.phone_code);
      fd.append('phone', form.phone);
      fd.append('website', form.website);
      fd.append('business_type_id', form.business_type_id);
      fd.append('order_accept_type', form.order_accept_type);
      fd.append('address', form.address);
      fd.append('about', form.about);
      if (form.lat !== '' && form.lat !== null) fd.append('lat', form.lat);
      if (form.lng !== '' && form.lng !== null) fd.append('lng', form.lng);

      // Logo (only if a new file was picked)
      if (form.logo instanceof File) fd.append('logo', form.logo);

      // New cover images
      coverFiles.forEach(file => { if (file instanceof File) fd.append('cover_images', file); });

      // Covers removed = originals no longer kept
      const removed = originalCovers.current.filter(url => !oldCoverImages.includes(url));
      if (removed.length) fd.append('remove_cover_images', removed.join(','));

      const res = await ApiService.request({ method: 'POST', url: 'partner/updateProfile', data: fd });
      if (res.data.status) {
        toast.success(res.data.message || 'Profile updated');
        setCoverFiles([]);
        getProfile();
      } else toast.error(res.data.message);
    } catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const handleDocPick = (key) => (e) => {
    const file = e.target.files?.[0] || null;
    setDocFiles(prev => ({ ...prev, [key]: file }));
  };

  const handleDocsSubmit = async (e) => {
    e.preventDefault();
    const hasFile = Object.values(docFiles).some(Boolean);
    if (!hasFile) { toast.error('Please select at least one document to upload'); return; }
    try {
      setSavingDocs(true);
      const fd = new FormData();
      DOC_FIELDS.forEach(({ key }) => { if (docFiles[key]) fd.append(key, docFiles[key]); });

      const res = await ApiService.request({ method: 'POST', url: 'partner/updateDocuments', data: fd });
      if (res.data.status) {
        toast.success(res.data.message || 'Documents updated');
        setDocuments(res.data.data?.documents || null);
        setDocFiles({ business_license: null, emirates_id: null, trade_name_certificate: null });
      } else toast.error(res.data.message);
    } catch { toast.error('Failed to update documents'); }
    finally { setSavingDocs(false); }
  };

  useEffect(() => {
    if (tab === 'bank' && !bankLoaded) getBankDetails();
  }, [tab]);

  const getBankDetails = async () => {
    try {
      const res = await ApiService.request({ method: 'GET', url: 'partner/getBankDetails' });
      if (res.data.status && res.data.data?.bank_details) {
        const b = res.data.data.bank_details;
        setBank({
          account_title:  b.account_title  || '',
          account_number: b.account_number || '',
          iban:           b.iban           || '',
          bank_name:      b.bank_name       || '',
          city:           b.city            || '',
        });
      }
      setBankLoaded(true);
    } catch { toast.error('Failed to load bank details'); }
  };

  const onBankChange = (key) => (e) => setBank(p => ({ ...p, [key]: e.target.value }));

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    try {
      setSavingBank(true);
      const res = await ApiService.request({ method: 'POST', url: 'partner/updateBankDetails', data: bank });
      if (res.data.status) toast.success(res.data.message || 'Bank details updated');
      else toast.error(res.data.message);
    } catch { toast.error('Failed to update bank details'); }
    finally { setSavingBank(false); }
  };

  const field = (label, key, type = 'text', required = false) => (
    <div className="col-md-6 fv-row mb-5">
      <label className={`${required ? 'required ' : ''}fs-6 fw-semibold mb-2`}>{label}</label>
      <input
        type={type}
        className="form-control form-control-solid"
        value={form[key]}
        onChange={onChange(key)}
        required={required}
      />
    </div>
  );

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div className="app-container container-xxl">
          <h1 className="page-heading text-dark fw-bold fs-3 my-0">Profile</h1>
        </div>
      </div>

      <div className="app-content flex-column-fluid">
        <div className="app-container container-xxl">
          {loading ? (
            <div className="text-center py-10"><Loader size={35} text="Loading" color="black" /></div>
          ) : (
            <div className="card">
              {/* Tabs */}
              <div className="card-header border-0 pt-4">
                <ul className="nav nav-tabs nav-line-tabs fs-6 border-0">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${tab === 'info' ? 'active text-base' : ''}`}
                      onClick={() => setTab('info')}
                    >
                      <i className="bi bi-building me-2"></i>Business Info
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${tab === 'documents' ? 'active text-base' : ''}`}
                      onClick={() => setTab('documents')}
                    >
                      <i className="bi bi-file-earmark-text me-2"></i>Documents
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${tab === 'bank' ? 'active text-base' : ''}`}
                      onClick={() => setTab('bank')}
                    >
                      <i className="bi bi-bank me-2"></i>Bank Details
                    </button>
                  </li>
                </ul>
              </div>

              <div className="card-body pt-6">

                {/* ── Business Info Tab ── */}
                {tab === 'info' && (
                  <form onSubmit={handleSubmit}>
                    {/* Logo */}
                    <div className="fv-row mb-8">
                      <label className="fs-6 fw-semibold mb-2 d-block">Logo</label>
                      <ImageUpload
                        name="logo"
                        onImageChange={handleLogoChange}
                        defaultPreviewUrl={profile?.logo}
                      />
                    </div>

                    <div className="row">
                      {field('Company Name', 'company_name', 'text', true)}
                      {field('Owner Name',   'owner_name')}
                      {field('Email',        'email', 'email', true)}

                      {/* Phone with code */}
                      <div className="col-md-6 fv-row mb-5">
                        <label className="fs-6 fw-semibold mb-2">Phone</label>
                        <div className="d-flex gap-2">
                          <input
                            type="text"
                            className="form-control form-control-solid"
                            style={{ maxWidth: 90 }}
                            placeholder="+971"
                            value={form.phone_code}
                            onChange={onChange('phone_code')}
                          />
                          <input
                            type="tel"
                            className="form-control form-control-solid"
                            value={form.phone}
                            onChange={onChange('phone')}
                          />
                        </div>
                      </div>

                      {/* Business type */}
                      <div className="col-md-6 fv-row mb-5">
                        <label className="fs-6 fw-semibold mb-2">Business Type</label>
                        <select
                          className="form-select form-select-solid"
                          value={form.business_type_id}
                          onChange={onChange('business_type_id')}
                        >
                          <option value="">Select business type</option>
                          {businessTypes.map(bt => (
                            <option key={bt._id} value={bt._id}>{bt.title}</option>
                          ))}
                        </select>
                      </div>

                      {field('Website', 'website', 'text')}

                      {/* Order Accept Type */}
                      <div className="col-md-6 fv-row mb-5">
                        <label className="fs-6 fw-semibold mb-2">Order Accept Type</label>
                        <select
                          className="form-select form-select-solid"
                          value={form.order_accept_type}
                          onChange={onChange('order_accept_type')}
                        >
                          <option value="Manual">Manual</option>
                          <option value="Auto">Auto</option>
                        </select>
                        <div className="text-muted fs-8 mt-2">
                          Auto: paid bookings are confirmed automatically. Manual: you accept each booking.
                        </div>
                      </div>
                    </div>

                    {/* About */}
                    <div className="fv-row mb-8">
                      <label className="fs-6 fw-semibold mb-2">About</label>
                      <textarea
                        className="form-control form-control-solid"
                        rows="4"
                        value={form.about}
                        onChange={onChange('about')}
                      />
                    </div>

                    {/* Cover images */}
                    <div className="fv-row mb-8">
                      <label className="fs-6 fw-semibold mb-2 d-block">Cover Images</label>
                      <FileDropzone
                        oldFiles={oldCoverImages}
                        updateOldImages={setOldCoverImages}
                        setFiles={setCoverFiles}
                      />
                    </div>

                    {/* Location map */}
                    <div className="fv-row mb-8">
                      <label className="fs-6 fw-semibold mb-2 d-block">Location</label>
                      <GoogleMapSelector
                        locationNew={mapLocation}
                        onLocationChanged={handleLocationChanged}
                      />
                    </div>

                    <div className="text-end">
                      <button type="submit" className="btn btn-sm btn-base text-white px-6" disabled={saving}>
                        {saving ? <Loader size={18} text="Saving" color="white" /> : 'Save Changes'}
                      </button>
                    </div>
                  </form>
                )}

                {/* ── Documents Tab ── */}
                {tab === 'documents' && (
                  <form onSubmit={handleDocsSubmit}>
                    <div className="row">
                      {DOC_FIELDS.map(({ key, label }) => {
                        const existingUrl = documents?.[key];
                        const picked = docFiles[key];
                        return (
                          <div key={key} className="col-md-6 fv-row mb-7">
                            <label className="fs-6 fw-semibold mb-2 d-block">{label}</label>

                            {existingUrl && !picked && (
                              <a href={existingUrl} target="_blank" rel="noreferrer"
                                className="d-inline-flex align-items-center mb-2 text-base fw-semibold">
                                <i className="bi bi-file-earmark-check me-2 fs-4"></i>View current
                              </a>
                            )}

                            <div className="d-flex align-items-center gap-3">
                              <input
                                type="file"
                                accept="image/png,image/jpeg,application/pdf"
                                className="form-control form-control-solid"
                                onChange={handleDocPick(key)}
                              />
                            </div>

                            {picked && (
                              <div className="text-muted fs-8 mt-2">
                                <i className="bi bi-paperclip me-1"></i>{picked.name}
                              </div>
                            )}
                            {!existingUrl && !picked && (
                              <div className="text-muted fs-8 mt-2">Not uploaded yet.</div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="text-muted fs-8 mb-4">Accepted formats: PNG, JPG, PDF.</div>

                    <div className="text-end">
                      <button type="submit" className="btn btn-sm btn-base text-white px-6" disabled={savingDocs}>
                        {savingDocs ? <Loader size={18} text="Saving" color="white" /> : 'Save Documents'}
                      </button>
                    </div>
                  </form>
                )}

                {/* ── Bank Details Tab ── */}
                {tab === 'bank' && (
                  !bankLoaded ? (
                    <div className="text-center py-10"><Loader size={30} text="Loading" color="black" /></div>
                  ) : (
                    <form onSubmit={handleBankSubmit}>
                      <div className="row">
                        {BANK_FIELDS.map(({ key, label }) => (
                          <div key={key} className="col-md-6 fv-row mb-5">
                            <label className="fs-6 fw-semibold mb-2">{label}</label>
                            <input
                              type="text"
                              className="form-control form-control-solid"
                              placeholder={label}
                              value={bank[key]}
                              onChange={onBankChange(key)}
                            />
                          </div>
                        ))}
                      </div>

                      <div className="text-muted fs-8 mb-4">
                        <i className="bi bi-info-circle me-1"></i>Bank details are used for your payouts.
                      </div>

                      <div className="text-end">
                        <button type="submit" className="btn btn-sm btn-base text-white px-6" disabled={savingBank}>
                          {savingBank ? <Loader size={18} text="Saving" color="white" /> : 'Save Bank Details'}
                        </button>
                      </div>
                    </form>
                  )
                )}

              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerProfile;
